import { Component, lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { useApp } from './app-context';

// Pages load when first opened, so the first download is only the shell and chats. Once the app is idle,
// every page is fetched in the background: the next tap is instant, and the service worker has them all
// cached for offline use.

const loaders: Array<() => Promise<unknown>> = [];
const RELOAD_KEY = 'da_chunk_reload';

/** After a deploy, an open tab may ask for page files that no longer exist: reload once to get the new ones. */
function isChunkError(err: unknown): boolean {
  const text = err instanceof Error ? `${err.name} ${err.message}` : String(err);
  return /dynamically imported module|Importing a module script failed|error loading dynamically|ChunkLoadError|Failed to fetch/i.test(text);
}

function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  location.reload();
  return true;
}

export function lazyPage<M, K extends keyof M>(load: () => Promise<M>, name: K): ComponentType {
  loaders.push(load);
  return lazy(async () => {
    try {
      return { default: (await load())[name] as ComponentType };
    } catch (err) {
      // Never resolves while the page reloads, so no error flashes first.
      if (navigator.onLine && isChunkError(err) && reloadOnce()) return new Promise<never>(() => undefined);
      throw err;
    }
  });
}

/** Fetches every page in the background when the browser is idle (skipped on data saver). */
export function prefetchPages(): void {
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (conn?.saveData) return;
  const run = () => {
    let i = 0;
    const next = () => {
      const load = loaders[i++];
      if (load) load().then(next, next);
    };
    next();
  };
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  if (idle) idle(run, { timeout: 4000 });
  else setTimeout(run, 2000);
}

function PageLoading() {
  const { i18n } = useApp();
  return (
    <div className="page-loading" role="status" aria-live="polite">
      <span className="page-loading-dot" aria-hidden="true" />
      <span className="visually-hidden">{i18n.t('app.loading')}</span>
    </div>
  );
}

function PageFailed() {
  const { i18n } = useApp();
  return (
    <div className="page-empty" role="alert">
      <p>{i18n.t('error.NETWORK')}</p>
      <button className="btn btn-primary" type="button" onClick={() => location.reload()}>
        {i18n.t('common.retry')}
      </button>
    </div>
  );
}

class PageErrorBoundary extends Component<{ children: ReactNode; resetKey: string }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidUpdate(prev: { resetKey: string }) {
    if (prev.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false });
  }

  override render() {
    return this.state.failed ? <PageFailed /> : this.props.children;
  }
}

/** Wraps lazily loaded pages: a quiet loading state, and a retry instead of a blank screen when a page cannot load. */
export function PageBoundary({ children, resetKey }: { children: ReactNode; resetKey: string }) {
  return (
    <PageErrorBoundary resetKey={resetKey}>
      <Suspense fallback={<PageLoading />}>{children}</Suspense>
    </PageErrorBoundary>
  );
}
